import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import MotionSection from '../components/ui/MotionSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiCheck } = FiIcons;

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await signup(formData.email, formData.password, formData.name);
      setStep(2); // Move to onboarding
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = () => {
    navigate('/app');
  };

  const handleGoogleSignup = () => {
    // Simulate Google OAuth
    alert('Google OAuth integration coming soon!');
  };

  if (step === 2) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen pt-20 flex items-center justify-center"
      >
        <div className="max-w-2xl w-full mx-4">
          <MotionSection className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Welcome to ColdMail AI!</h1>
            <p className="text-light/60">Let's get you set up in 3 quick steps</p>
          </MotionSection>

          <MotionSection className="gradient-border rounded-xl p-6">
            <div className="gradient-border-inner p-6">
              <div className="space-y-8">
                {/* Step 1 */}
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">What's your niche?</h3>
                    <input
                      type="text"
                      placeholder="e.g., SaaS, Real Estate, Marketing Agency"
                      className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Who do you usually email?</h3>
                    <select className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary">
                      <option>Founders & CEOs</option>
                      <option>Marketing Directors</option>
                      <option>Sales Managers</option>
                      <option>HR Professionals</option>
                      <option>Small Business Owners</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Preferred email tone?</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['Friendly', 'Formal', 'Bold', 'Conversational'].map(tone => (
                        <button
                          key={tone}
                          className="px-4 py-2 border border-primary/20 rounded-lg hover:border-primary transition-colors text-left"
                        >
                          {tone}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={completeOnboarding}
                  className="w-full btn-primary py-3 rounded-lg text-white font-medium"
                >
                  Complete Setup & Start Generating
                </button>
              </div>
            </div>
          </MotionSection>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20 flex items-center justify-center"
    >
      <div className="max-w-md w-full mx-4">
        <MotionSection className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Create your account</h1>
          <p className="text-light/60">Start writing better cold emails today</p>
        </MotionSection>

        <MotionSection className="gradient-border rounded-xl p-6">
          <div className="gradient-border-inner p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Full name
                </label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email address
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    className="w-full pl-10 pr-12 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-light/40 hover:text-light"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  <div className={`flex items-center space-x-2 text-xs ${formData.password.length >= 6 ? 'text-primary' : 'text-light/40'}`}>
                    <SafeIcon icon={FiCheck} className="w-3 h-3" />
                    <span>At least 6 characters</span>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    required
                    className="w-full pl-10 pr-12 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-light/40 hover:text-light"
                  >
                    <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex items-start">
                <input type="checkbox" required className="mr-3 mt-1" />
                <span className="text-sm text-light/60">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-primary/20" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-dark text-light/60">Or continue with</span>
                </div>
              </div>

              <button
                onClick={handleGoogleSignup}
                className="mt-4 w-full flex items-center justify-center px-4 py-3 border border-primary/20 rounded-lg hover:border-primary transition-colors"
              >
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  className="w-5 h-5 mr-3"
                />
                Sign up with Google
              </button>
            </div>

            <p className="mt-6 text-center text-sm text-light/60">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </MotionSection>
      </div>
    </motion.div>
  );
};

export default Signup;